import { TooltipDirective } from 'src/app/directives/tooltip.directive';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  faArrowLeft,
  faArrowRight,
  faBox,
  faEdit,
  faPlusCircle,
  faTicket,
  faUsers,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import { EnumAcl } from '@simplyfly/shared';
import { EnumEnquiryType } from '@simplyfly/shared';
import { IAirline } from '@simplyfly/shared';
import { EnumCrewType, ICrew } from '@simplyfly/shared';
import { IEnquiryFlight } from '@simplyfly/shared';
import { IEnquiry } from '@simplyfly/shared';
import { IAirportSupervisor } from '@simplyfly/shared';
import { AclService } from 'src/app/services/acl.service';
import { AirlineService } from 'src/app/services/airlines/airlines.service';
import { AirportSupervisorService } from 'src/app/services/airport-supervisors/airport-supervisors.service';
import { CrewService } from 'src/app/services/crews/crews.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';


import { TranslateModule } from '@ngx-translate/core';
import { CrewEditComponent } from '../../../admin/crews/crew-edit/crew-edit.component';
import { AirportSupervisorEditComponent } from '../../../admin/airport-supervisors/airport-supervisor-edit/airport-supervisor-edit.component';

@Component({
    selector: 'app-enquiry-flight-edit-crew',
    templateUrl: './enquiry-flight-edit-crew.component.html',
    styleUrls: ['./enquiry-flight-edit-crew.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      TooltipDirective,
      FormsModule,
      ReactiveFormsModule,
      FaIconComponent,
      CrewEditComponent,
      AirportSupervisorEditComponent,
      TranslateModule
    ]
})
export class EnquiryFlightEditCrewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() enquiry: IEnquiry | null = null;
  @Input() enquiryFlight: IEnquiryFlight | null = null;
  @Output() goPrev: EventEmitter<void> = new EventEmitter();
  @Output() saveAndGoNext: EventEmitter<IEnquiryFlight> = new EventEmitter();

  @ViewChild('modalCaptain', { static: false }) modalCaptainElement: ElementRef;
  @ViewChild('modalCoPilot', { static: false }) modalCoPilotElement: ElementRef;
  @ViewChild('modalStewardess', { static: false }) modalStewardessElement: ElementRef;
  @ViewChild('modalSupervisor', { static: false }) modalSupervisorElement: ElementRef;

  form: FormGroup = this.resetForm();

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faUserTie = faUserTie;
  faPlusCircle = faPlusCircle;
  faEdit = faEdit;

  EnumEnquiryType = EnumEnquiryType;
  EnumCrewType = EnumCrewType;
  EnumAcl = EnumAcl;

  loadingCrewsForAirline = false;
  loadingDepartSupervisorsForAirport = false;
  loadingArrivalSupervisorsForAirport = false;
  editingCaptain: ICrew | null = null;
  editingCoPilot: ICrew | null = null;
  editingStewardess: ICrew | null = null;
  editingSupervisor: IAirportSupervisor | null = null;
  crewsByType: { [type: string]: ICrew[] } = {};
  airlinesObj: { [id: string]: IAirline | null } = {};
  departSupervisors: IAirportSupervisor[] = [];
  arrivalSupervisors: IAirportSupervisor[] = [];

  private subscriptions = new Subscription();
  private subscriptionsAirline = new Subscription();
  private subscriptionsCrew = new Subscription();
  supervisorLegForModal: 'depart' | 'arrival' | null = null;

  constructor(
    private crewService: CrewService,
    private airportSupervisorService: AirportSupervisorService,
    private airlineService: AirlineService,
    private aclService: AclService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.applyEnquiryFlight();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.enquiryFlight &&
      (!changes.enquiryFlight.previousValue ||
        changes.enquiryFlight.previousValue !== changes.enquiryFlight.currentValue)
    ) {
      this.applyEnquiryFlight();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.subscriptionsAirline.unsubscribe();
    this.subscriptionsCrew.unsubscribe();
  }

  get salesH24Phone(): FormControl {
    return this.form.get('salesH24Phone') as FormControl;
  }
  get opsH24Phone(): FormControl {
    return this.form.get('opsH24Phone') as FormControl;
  }
  get captainId(): FormControl {
    return this.form.get('captainId') as FormControl;
  }
  get coPilotId(): FormControl {
    return this.form.get('coPilotId') as FormControl;
  }

  hasCrewObservation(crew: ICrew): boolean {
    return !!crew.comment?.trim();
  }

  getPilotObservation(type: EnumCrewType.captain | EnumCrewType.coPilot, id: string | null): string | null {
    if (!id) {
      return null;
    }

    const crew = this.crewsByType[type]?.find((item: ICrew) => item.id === id);
    return crew && this.hasCrewObservation(crew) ? crew.comment : null;
  }

  get stewardessEnabled(): FormControl {
    return this.form.get('stewardessEnabled') as FormControl;
  }
  get stewardessIds(): FormControl {
    return this.form.get('stewardessIds') as FormControl;
  }
  get departSupervisorEnabled(): FormControl {
    return this.form.get('departSupervisorEnabled') as FormControl;
  }
  get departSupervisorIds(): FormControl {
    return this.form.get('departSupervisorIds') as FormControl;
  }
  get arrivalSupervisorEnabled(): FormControl {
    return this.form.get('arrivalSupervisorEnabled') as FormControl;
  }
  get arrivalSupervisorIds(): FormControl {
    return this.form.get('arrivalSupervisorIds') as FormControl;
  }

  applyEnquiryFlight(): void {
    this.form = this.resetForm();

    if (this.enquiryFlight) {
      for (const field in this.form.value) {
        if (typeof this.enquiryFlight[field] !== 'undefined') {
          switch (field) {
            default:
              this.form.get(field).setValue(this.enquiryFlight[field]);
              break;
          }
        }
      }

      if (this.enquiryFlight.airlineId) {
        this.loadAirline(this.enquiryFlight.airlineId);

        this.loadAirlineCrewsAndSupervisors();
        this.refreshSalesOrOpsH24PhoneFromAirlineIfMissing();
      }

      this.updatedStewardessEnabled();
      this.updatedSupervisorEnabled('depart');
      this.updatedSupervisorEnabled('arrival');

      this.form.enable();
    }
  }

  resetForm(): FormGroup {
    return new FormGroup({
      salesH24Phone: new FormControl(null),
      opsH24Phone: new FormControl(null),
      captainId: new FormControl(null),
      coPilotId: new FormControl(null),
      stewardessEnabled: new FormControl(false),
      stewardessIds: new FormControl([]),
      departSupervisorEnabled: new FormControl(false),
      departSupervisorIds: new FormControl([]),
      arrivalSupervisorEnabled: new FormControl(false),
      arrivalSupervisorIds: new FormControl([])
    });
  }

  prev(): void {
    this.goPrev.emit();
  }

  async saveAndNext(): Promise<void> {
    if (this.form.valid) {
      if (this.enquiryFlight?.airlineId && this.airlinesObj[this.enquiryFlight.airlineId]) {
        let updateAirline = false;
        const airlineDataToUpdate: IAirline = {
          id: this.enquiryFlight.airlineId
        } as IAirline;

        if (
          (!this.airlinesObj[this.enquiryFlight.airlineId].salesH24Phone &&
            this.form.value.salesH24Phone) ||
          this.form.value.salesH24Phone !==
            this.airlinesObj[this.enquiryFlight.airlineId].salesH24Phone
        ) {
          updateAirline = true;
          airlineDataToUpdate.salesH24Phone = this.form.value.salesH24Phone;
        }

        if (
          (!this.airlinesObj[this.enquiryFlight.airlineId].opsH24Phone &&
            this.form.value.opsH24Phone) ||
          this.form.value.opsH24Phone !== this.airlinesObj[this.enquiryFlight.airlineId].opsH24Phone
        ) {
          updateAirline = true;
          airlineDataToUpdate.opsH24Phone = this.form.value.opsH24Phone;
        }

        if (updateAirline) {
          console.log('updating salesH24Phone or opsH24Phone on airline');

          // Update salesH24Phone or opsH24Phone on airline
          this.loaderService.presentLoader();

          try {
            await this.airlineService.update(airlineDataToUpdate);

            await this.loaderService.hideLoaderImmediately();
          } catch (err) {
            await this.loaderService.hideLoaderOnFailure(err);
          }
        }
      }

      this.saveAndGoNext.emit(this.form.value);
    }
  }

  updatedStewardessEnabled(): void {
    if (this.stewardessEnabled.value) {
      this.stewardessIds.addValidators(Validators.required);
    } else {
      this.stewardessIds.clearValidators();
      this.stewardessIds.setValue([]);
    }

    this.stewardessIds.updateValueAndValidity();
  }

  updatedSupervisorEnabled(leg: 'depart' | 'arrival'): void {
    const enabledControl =
      leg === 'depart' ? this.departSupervisorEnabled : this.arrivalSupervisorEnabled;
    const idsControl = leg === 'depart' ? this.departSupervisorIds : this.arrivalSupervisorIds;

    if (enabledControl.value) {
      idsControl.addValidators(Validators.required);
    } else {
      idsControl.clearValidators();
      idsControl.setValue([]);
    }

    idsControl.updateValueAndValidity();
  }

  loadAirline(airlineId: string): void {
    if (typeof this.airlinesObj[airlineId] === 'undefined') {
      this.airlinesObj[airlineId] = null;

      this.subscriptionsAirline.add(
        this.airlineService.getFromId(airlineId).subscribe((airline: IAirline | null) => {
          this.airlinesObj[airlineId] = airline;

          this.refreshSalesOrOpsH24PhoneFromAirlineIfMissing();
        })
      );
    }
  }

  loadAirlineCrewsAndSupervisors(): void {
    if (this.enquiryFlight.airlineId) {
      if (this.subscriptionsCrew) {
        this.subscriptionsCrew.unsubscribe();
      }

      this.subscriptionsCrew = new Subscription();

      this.loadingCrewsForAirline = true;

      this.subscriptionsCrew.add(
        this.crewService.getForAirline(this.enquiryFlight.airlineId).subscribe((crews: ICrew[]) => {
          this.crewsByType = {};

          for (const crew of crews) {
            if (typeof this.crewsByType[crew.type] === 'undefined') {
              this.crewsByType[crew.type] = [];
            }

            this.crewsByType[crew.type].push(crew);
          }

          this.loadingCrewsForAirline = false;
        })
      );

      this.loadSupervisorsForAirports();
    }
  }

  loadSupervisorsForAirports(): void {
    if (!this.enquiryFlight) {
      return;
    }

    this.loadingDepartSupervisorsForAirport = true;
    this.loadingArrivalSupervisorsForAirport = true;
    this.departSupervisors = [];
    this.arrivalSupervisors = [];

    if (this.enquiryFlight.airportDepartId) {
      this.subscriptionsCrew.add(
        this.airportSupervisorService
          .getForAirport(this.enquiryFlight.airportDepartId)
          .subscribe((supervisors: IAirportSupervisor[]) => {
            this.departSupervisors = supervisors;
            this.loadingDepartSupervisorsForAirport = false;
          })
      );
    } else {
      this.loadingDepartSupervisorsForAirport = false;
    }

    if (this.enquiryFlight.airportArrivalId) {
      this.subscriptionsCrew.add(
        this.airportSupervisorService
          .getForAirport(this.enquiryFlight.airportArrivalId)
          .subscribe((supervisors: IAirportSupervisor[]) => {
            this.arrivalSupervisors = supervisors;
            this.loadingArrivalSupervisorsForAirport = false;
          })
      );
    } else {
      this.loadingArrivalSupervisorsForAirport = false;
    }
  }

  toggleStewardessId(crewId: string): void {
    const values: string[] = [...this.stewardessIds.value];
    const index: number = values.indexOf(crewId);

    if (index === -1) {
      values.push(crewId);
    } else {
      values.splice(index, 1);
    }

    this.stewardessIds.setValue(values);
    this.stewardessIds.updateValueAndValidity();
  }

  toggleSupervisorId(leg: 'depart' | 'arrival', supervisorId: string): void {
    const control = leg === 'depart' ? this.departSupervisorIds : this.arrivalSupervisorIds;
    const values: string[] = [...control.value];
    const index: number = values.indexOf(supervisorId);

    if (index === -1) {
      values.push(supervisorId);
    } else {
      values.splice(index, 1);
    }

    control.setValue(values);
    control.updateValueAndValidity();
  }

  addCaptain(): void {
    this.editingCaptain = null;

    window.$(this.modalCaptainElement.nativeElement).modal('show');
  }

  editCaptain(): void {
    const captainId = this.captainId.value;

    if (!captainId) {
      return;
    }

    this.editingCaptain =
      this.crewsByType[EnumCrewType.captain]?.find(
        (captain: ICrew) => captain.id === captainId
      ) ?? ({ id: captainId } as ICrew);

    window.$(this.modalCaptainElement.nativeElement).modal('show');
  }

  dismissCaptainModal(newId: string | null = null): void {
    if (newId) {
      this.captainId.setValue(newId);
      this.captainId.updateValueAndValidity();
    }

    window.$(this.modalCaptainElement.nativeElement).modal('hide');
    this.editingCaptain = null;
  }

  addCoPilot(): void {
    this.editingCoPilot = null;

    window.$(this.modalCoPilotElement.nativeElement).modal('show');
  }

  editCoPilot(): void {
    const coPilotId = this.coPilotId.value;

    if (!coPilotId) {
      return;
    }

    this.editingCoPilot =
      this.crewsByType[EnumCrewType.coPilot]?.find(
        (coPilot: ICrew) => coPilot.id === coPilotId
      ) ?? ({ id: coPilotId } as ICrew);

    window.$(this.modalCoPilotElement.nativeElement).modal('show');
  }

  dismissCoPilotModal(newId: string | null = null): void {
    if (newId) {
      this.coPilotId.setValue(newId);
      this.coPilotId.updateValueAndValidity();
    }

    window.$(this.modalCoPilotElement.nativeElement).modal('hide');
    this.editingCoPilot = null;
  }

  addStewardess(): void {
    this.editingStewardess = null;

    window.$(this.modalStewardessElement.nativeElement).modal('show');
  }

  editStewardess(stewardessId: string): void {
    this.editingStewardess =
      this.crewsByType[EnumCrewType.stewardess]?.find(
        (stewardess: ICrew) => stewardess.id === stewardessId
      ) ?? ({ id: stewardessId } as ICrew);

    window.$(this.modalStewardessElement.nativeElement).modal('show');
  }

  dismissStewardessModal(newId: string | null = null): void {
    const stewardessIds: string[] = [...this.stewardessIds.value];

    if (newId) {
      stewardessIds.push(newId);

      this.stewardessIds.setValue(stewardessIds);
      this.stewardessIds.updateValueAndValidity();
    }

    window.$(this.modalStewardessElement.nativeElement).modal('hide');
    this.editingStewardess = null;
  }

  addSupervisor(leg: 'depart' | 'arrival'): void {
    this.editingSupervisor = null;
    this.supervisorLegForModal = leg;

    window.$(this.modalSupervisorElement.nativeElement).modal('show');
  }

  editSupervisor(supervisorId: string): void {
    if (!this.supervisorLegForModal) {
      return;
    }

    const list =
      this.supervisorLegForModal === 'depart' ? this.departSupervisors : this.arrivalSupervisors;

    this.editingSupervisor =
      list.find((supervisor: IAirportSupervisor) => supervisor.id === supervisorId) ??
      ({ id: supervisorId } as IAirportSupervisor);

    window.$(this.modalSupervisorElement.nativeElement).modal('show');
  }

  dismissSupervisorModal(newId: string | null = null): void {
    if (newId && this.supervisorLegForModal) {
      const control =
        this.supervisorLegForModal === 'depart'
          ? this.departSupervisorIds
          : this.arrivalSupervisorIds;
      const supervisorIds: string[] = [...control.value];

      supervisorIds.push(newId);

      control.setValue(supervisorIds);
      control.updateValueAndValidity();
    }

    this.supervisorLegForModal = null;
    window.$(this.modalSupervisorElement.nativeElement).modal('hide');
    this.editingSupervisor = null;
  }

  getSupervisors(leg: 'depart' | 'arrival'): IAirportSupervisor[] {
    return leg === 'depart' ? this.departSupervisors : this.arrivalSupervisors;
  }

  hasAclAccess(id: EnumAcl): boolean {
    return this.aclService.hasAclAccess(id);
  }

  refreshSalesOrOpsH24PhoneFromAirlineIfMissing(): void {
    if (this.enquiryFlight.airlineId) {
      if (
        this.airlinesObj[this.enquiryFlight.airlineId]?.salesH24Phone &&
        !this.form.value.salesH24Phone
      ) {
        this.salesH24Phone.setValue(this.airlinesObj[this.enquiryFlight.airlineId]?.salesH24Phone);
        this.salesH24Phone.updateValueAndValidity();
      }

      if (
        this.airlinesObj[this.enquiryFlight.airlineId]?.opsH24Phone &&
        !this.form.value.opsH24Phone
      ) {
        this.opsH24Phone.setValue(this.airlinesObj[this.enquiryFlight.airlineId]?.opsH24Phone);
        this.opsH24Phone.updateValueAndValidity();
      }
    }
  }
}
